"use client";

import {
  MorphingDialog,
  MorphingDialogClose,
  MorphingDialogContainer,
  MorphingDialogContent,
  MorphingDialogImage,
  MorphingDialogSection,
  MorphingDialogSubtitle,
  MorphingDialogTitle,
  MorphingDialogTrigger,
} from "@/components/motion-primitives/morphing-dialog";
import { useViewerLanguage } from "@/lib/i18n";
import {
  emitModuleInteraction,
  emitModuleSurface,
} from "@/lib/module-interaction";
import { cn } from "@/lib/utils";
import { ImageModule } from "@/types/modules";
import { renderInlineMarkdown, stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import React, { useEffect, useRef } from "react";

function isRenderableImageSrc(src: unknown): src is string {
  return (
    typeof src === "string" &&
    (src.startsWith("http://") ||
      src.startsWith("https://") ||
      src.startsWith("data:") ||
      src.startsWith("/"))
  );
}

export default function ModuleImage({
  data,
  grayscale,
  className,
}: {
  data: ImageModule;
  grayscale?: boolean;
  className?: string;
}): React.JSX.Element | null {
  const triggerRef = useRef<HTMLButtonElement>(null!);
  const { t } = useViewerLanguage();

  // The dialog primitive exposes no open callback, but it mirrors its open
  // state onto the trigger as aria-expanded. Observing that attribute catches
  // every way the lightbox can be opened, including Enter/Space, which the
  // trigger handles in onKeyDown without emitting a click.
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const readOpen = () => trigger.getAttribute("aria-expanded") === "true";
    let wasOpen = readOpen();
    const observer = new MutationObserver(() => {
      const isOpen = readOpen();
      if (isOpen && !wasOpen) {
        emitModuleInteraction(trigger, "click", { kind: "card", index: 0 });
        emitModuleSurface(trigger, "open", "lightbox", "overlay");
      } else if (!isOpen && wasOpen) {
        emitModuleSurface(trigger, "close", "lightbox", "overlay");
      }
      wasOpen = isOpen;
    });
    observer.observe(trigger, {
      attributes: true,
      attributeFilter: ["aria-expanded"],
    });
    return () => {
      observer.disconnect();
      if (wasOpen) emitModuleSurface(trigger, "close", "lightbox", "overlay");
    };
  }, []);

  if (!isRenderableImageSrc(data?.src)) {
    return null;
  }
  return (
    <div
      aria-label="image-module"
      className={cn(
        "w-full flex flex-col justify-start items-start gap-2 self-stretch min-h-0",
        className,
      )}
    >
      <MorphingDialog
        transition={{ type: "tween", bounce: 0.1, duration: 0.25 }}
      >
        <MorphingDialogTrigger
          triggerRef={triggerRef}
          style={{ borderRadius: "8px" }}
          className="relative w-full overflow-hidden border border-neutral-100 bg-neutral-50 dark:border-white/[0.10] dark:bg-white/[0.04] cursor-pointer"
        >
          <MorphingDialogImage
            src={data.src}
            alt={data.alt || t("Image")}
            className={cn("w-full h-auto object-cover", grayscale && "grayscale")}
          />
        </MorphingDialogTrigger>
        <MorphingDialogContainer>
          <MorphingDialogContent
            style={{ borderRadius: "10px" }}
            className="pointer-events-auto relative flex h-auto w-full flex-col overflow-hidden bg-white dark:bg-neutral-800 sm:w-[600px] mx-4"
          >
            <div className="relative z-10">
              <MorphingDialogImage
                src={data.src}
                alt={data.alt || t("Image")}
                className={cn("w-full", grayscale && "grayscale")}
              />
            </div>
            {(data.alt || data.description) && (
              <div className="relative z-0 flex flex-col pt-3 pb-4">
                {data.alt && (
                  <MorphingDialogSection>
                    <MorphingDialogTitle
                      className={`text-sm font-medium text-neutral-900 dark:text-neutral-100 px-4 pb-1 ${MODULE_LABEL_WRAP.heading}`}
                    >
                      {stripInlineMarkdown(data.alt)}
                    </MorphingDialogTitle>
                  </MorphingDialogSection>
                )}
                {data.description && (
                  <MorphingDialogSubtitle
                    className={`text-xs text-neutral-500 dark:text-neutral-400 ${MODULE_LABEL_WRAP.body}`}
                  >
                    {renderInlineMarkdown(data.description)}
                  </MorphingDialogSubtitle>
                )}
              </div>
            )}
            <MorphingDialogClose className="text-neutral-400 top-3 right-3 z-20" />
          </MorphingDialogContent>
        </MorphingDialogContainer>
      </MorphingDialog>

      {data.description && (
        <p
          className={`text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ${MODULE_LABEL_WRAP.body}`}
        >
          {renderInlineMarkdown(data.description)}
        </p>
      )}
    </div>
  );
}
